<template>
  <main id="main-content">
    <ApiLoading v-if="apiStatus.refData.loading || apiStatus.refData.fail" :apiError="apiStatus.refData.fail" />
    <div class="feature-landing-page" v-else>
      <div class="content-page-banner">
        <div class="banner-content">
          <h1>{{ $route.meta.title }}</h1>
          <div class="underline"></div>
          <div v-html="refData.withdrawRebateHeadingSubtitleText.informationText" />
        </div>
      </div>
      <Alert v-if="submitPolicyError" :message="ErrorStrings.SUBMIT_POLICY_API_ERROR" alertType="error"/> 
      <section class="multi-step-form">
        <MultiStepFormNavigation
          :currentPage="currentPage"
          :links="navLinks"
          @linkClicked="updateStep"
          :disabled="apiStatus.submit.loading || apiStatus.submit.fail || apiStatus.premium.loading"
        />
        <ApiLoading v-if="apiStatus.submit.loading || apiStatus.submit.fail || apiStatus.premium.loading" :apiError="apiStatus.submit.fail" />
        <div class="form-contents" v-else>
          <template v-if="isFirstPage">
            <SectionSeparatorWrapper headerText="Rebate withdrawal" headerRequiredFieldsText="Indicates required field">
              <section>
                <FormField labelText="Rebate applicant">
                  <p>{{ rebateApplicantName }}</p>
                </FormField>
                <FormField labelText="Current rebate registration date" :required="false">
                  <p>{{ reverseFormatFormDate(membershipDetails.rebate.rebateCommencementDate) }}</p>
                </FormField>
                <FormField labelText="Effective withdrawal date" :required="false">
                  <p>{{ reverseFormatFormDate(getCurrentDate()) }}</p>
                </FormField>
              </section>
            </SectionSeparatorWrapper>
          </template>

          <template v-else-if="isLastPage">
            <ReviewAndSubmitComponent :updateKey="false">
              <h3 class="review-subheading">Rebate information before rebate withdrawal</h3>
              <table class="review-and-submit-table current-rebate-details-table">
                <tbody>
                  <tr>
                    <th>Rebate applicant</th>
                    <td>{{ rebateApplicantName }}</td>
                  </tr>
                  <tr>
                    <th>Rebate registration date</th>
                    <td>{{ reverseFormatFormDate(membershipDetails.rebate.rebateCommencementDate) }}</td>
                  </tr>
                  <tr>
                    <th>Effective date of change</th>
                    <td>{{ reverseFormatFormDate(getCurrentDate()) }}</td>
                  </tr>
                </tbody>
              </table>

              <template v-if="currentPremium && newEstimatedPremium && apiStatus.premium.fail === false">
                <FormField noLabelOverride :helperText="refData.withdrawRebateReviewAndSubmitMyPremiumCalloutText.informationText">
                  <h3 class="review-subheading">My Premium</h3>
                  <table class="review-and-submit-table">
                    <tbody>
                      <tr>
                        <th>Current premium</th>
                        <td>{{ `${formatAsCurrency(currentPremium)} / ${mapPaymentFrequencyFromRecurringToSingleton(membershipInfo.frequency)}` }}</td>
                      </tr>
                      <tr class="info">
                        <th>New premium</th>
                        <td>{{ `${formatAsCurrency(newEstimatedPremium)} / ${mapPaymentFrequencyFromRecurringToSingleton(membershipInfo.frequency)}` }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p v-html="refData.withdrawRebateReviewAndSubmitNoteText.informationText" />
                </FormField>
              </template>
              <template v-else>
                <Alert
                  alertType="info"
                  message="We are unable to advise you of your estimated new premium. If you would still like to proceed with the change, your new premium will be sent to you in your certificate of cover."
                />
              </template>
            </ReviewAndSubmitComponent>
          </template>

          <div class="form-page-nav">
            <section class="form-buttons">
              <button id="back-button" v-if="isLastPage" class="secondary" @click="handleBackButton">Back</button>
              <button id="next-button" @click.prevent="handleNextButton">
                {{ isFirstPage ? 'Next' : 'Submit' }}
              </button>
            </section>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import { MultiStepFormNavigation, ApiLoading, SectionSeparatorWrapper, FormField, ReviewAndSubmitComponent, Alert } from '@/components';
import { getCurrentDate, reverseFormatFormDate, SubmitPolicyError, SubmitPolicyInProcessError, SubmitPolicyTimeoutError } from '@general/js/utils';
import RebateServices from '@/services/MyCover/Rebates';
import { REBATES_MEMBER_PORTAL } from '@/router/routeNames';
import { RebateTier } from '@general/js/enums';
import { ErrorStrings } from '@general/js/constants';
import CommonServices from '@/services/Common';
import RebateMixins from './RebateMixins';

export default {
  name: 'WithdrawRebate',
  components: { MultiStepFormNavigation, ApiLoading, SectionSeparatorWrapper, FormField, ReviewAndSubmitComponent, Alert },
  mixins: [RebateMixins],

  data() {
    return {
      currentPage: 1,
      pages: {
        REBATE_WITHDRAWAL: {
          pageTitle: 'Rebate withdrawal',
          pageNumber: 1,
          isInvalid: false,
        },
        REVIEW_AND_SUBMIT: {
          pageTitle: 'Review and submit',
          pageNumber: 2,
          isInvalid: false,
        },
      },
      apiStatus: {
        refData: { loading: false, fail: false },
        submit: { loading: false, fail: false },
        premium: { loading: false, fail: false },
      },
      submitPolicyError: false,
      refData: {},
      ErrorStrings,
    };
  },

  beforeMount() {
    this.fetchRefData();
    this.fetchMembershipDetails(this.apiStatus.submit);
  },

  watch: {
    currentPage(newVal) {
      if (newVal === this.pages.REVIEW_AND_SUBMIT.pageNumber) {
        this.fetchNewPremium(RebateTier.NONE, this.apiStatus.premium);
      }
    },
  },

  computed: {
    isFirstPage() {
      return this.currentPage === this.pages.REBATE_WITHDRAWAL.pageNumber;
    },

    isLastPage() {
      return this.currentPage === this.pages.REVIEW_AND_SUBMIT.pageNumber;
    },

    rebateApplicantName() {
      if (this.membershipDetails?.rebateApplicant) {
        const rebateApplicant = this.membershipDetails.rebateApplicant;
        return `${rebateApplicant.firstName}${rebateApplicant.middleName ? ` ${rebateApplicant.middleName}` : ''} ${rebateApplicant.lastName}`;
      }

      return '';
    },
  },

  methods: {
    async fetchRefData() {
      this.apiStatus.refData.loading = true;
      this.apiStatus.refData.fail = false;
      try {
        this.refData = await RebateServices.getWithdrawRebateRefData();
      } catch (error) {
        this.apiStatus.refData.fail = true;
        logger.log('Error fetching update rebate ref data', error);
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },

    async withdrawRebate() {
      this.apiStatus.submit.loading = true;
      this.apiStatus.submit.fail = false;
      this.submitPolicyError = false;
      this.updateSubmitPolicyInProcess(false);
      try {
        const payload = {
          currentDate: getCurrentDate(),
          effectiveWithdrawalDate: getCurrentDate(),
        };

        const response = await RebateServices.submitRebateWithdrawal(this.memberNumber, payload);

        const params = {
          newPremium: response.newPremium,
          currentPayFrequency: response.currentPayFrequency,
        };

        this.navigateToPage(REBATES_MEMBER_PORTAL.REBATE_UPDATE_SUCCESS, params);
      } catch (error) {
        logger.error(error);
        if (error instanceof SubmitPolicyError) {
          this.submitPolicyError = true;
        }
        if (error instanceof SubmitPolicyInProcessError || error instanceof SubmitPolicyTimeoutError) {
          this.updateSubmitPolicyInProcess(true);

          // Raise work item with payload only for timeout error
          if (error instanceof SubmitPolicyTimeoutError) {
            const payload = {
              currentDate: getCurrentDate(),
              effectiveWithdrawalDate: getCurrentDate(),
            };

            const request = {
              functionName: "add-update-rebate",
              workItemCategory: "OTHERS",
              title: `Submission Timeout for ${this.$route?.params?.policyNumber} in Member Portal`,
              payload,
            };

            await CommonServices.raiseWorkItem(this.$route?.params?.policyNumber, request);
          }

          const params = {
            submitPolicyInProcessHeader: ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.replacePlaceholder(ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.HEADER, 'rebate withdrawal'),
            submitPolicyInProcessSubHeader: ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.SUBHEADER,
          };

          // Redirect to success page
           this.navigateToPage(REBATES_MEMBER_PORTAL.REBATE_UPDATE_SUCCESS, params);

        } else {
          this.apiStatus.submit.fail = true;
        }
      } finally {
        this.apiStatus.submit.loading = false;
      }
    },

    handleNextButton() {
      if (this.isFirstPage) {
        this.currentPage = this.pages.REVIEW_AND_SUBMIT.pageNumber;
      } else {
        this.withdrawRebate();
      }
    },

    handleBackButton() {
      this.currentPage = this.pages.REBATE_WITHDRAWAL.pageNumber;
    },

    getCurrentDate,
    reverseFormatFormDate,
  },
};
</script>

<style lang="scss" scoped></style>
